// Exported methods to connect the BIMSearch UI to the engine and viewer

import math from "mathjs";
import { BimSearch } from "./BimSearch";
import { BimsearchViewer } from "./BimsearchViewer";
import {
    logCreateSchedule,
    logSelectCategory,
    logCreateTemplate,
    logTableLineSelected,
    logTemplate,
    logCreateSelectionSet,
    logOpenSelectionSet,
    logError
} from "./IAmecLog";
import { DataForMenus, valueTypes } from "./ResultClasses";
import { Collection, customColumn, ColorSchemeGradientData, ColorConditions, ColorCondition, conditiontypes } from "./TemplatesAndCollections";

// Method to be called when the model first open
// parameters:
// itemStruct - model item (vertion or item) - struct received from getNextLevel
// returnMethod - method called when the model is done loading will pass 2 parameters, DataForMenus object, and header for the left menu
// no longer used, doubleClickMethod - method that will be called whenever a user double click of element and should change the left menu selection, pass DataForMenus as parameter
// divElement - ui div element for autodesk viewer
// isTranslateModelEnabled
// leafText - opened element name recieved from getNextLevel, only used for adding version to header
export async function loadModel(
    itemStruct,
    returnMethod,
    divElement,
    isTranslateModelEnabled,
    leafText = null
) {
    bimSearch = new BimSearch();
    bimSearch.isTranslateModelEnabled = isTranslateModelEnabled
    
    const modelId = itemStruct.idToOpen || itemStruct.id;
    const viewer = new BimsearchViewer(
        itemStruct,
        divElement,
        bimSearch,
        returnMethod,
        leafText
    );
    
    // Store viewer by modelId for multi-model support
    viewersByModelId[modelId] = viewer;
    // Also set global for backward compatibility
    bimSearchViewer = viewer;
}

// Register the method that gets called when selection changed
// onSelectionChangeQuickStats - method called whenever the selection is changed, should effect the quick count widget, pass array of QuickDataField as parameter
export async function registerSelectionChangeMethod(callback) {
    bimSearchViewer.onSelectionChangeQuickStats = callback;
}

// Registers the passed callbacks to the bimsearchviewer which passes them to viewer's ToolbarExtension.
//      callbacksObject - an object containing handler functions.
export async function registerCustomParametersMethods(callbacksObject) {
    bimSearchViewer.registerCustomParametersMethods(callbacksObject)
}

// Register the timebar callback methods
// open and close
// need to be called in the same time as the other register methods
export async function registerTimebar(openMethod, closeMethod) {
    bimSearchViewer.registerTimebar(openMethod, closeMethod);
}

// Sync Autodesk toolbar button state (used when timeline is closed not via the toolbar button)
export function setTimebarButtonActive(isActive) {
    try {
        bimSearchViewer?.setTimebarButtonActive?.(!!isActive);
    } catch {}
}

// Unregister the method that gets called when selection changed
export async function unRegisterSelectionChangeMethod() {
    bimSearchViewer.onSelectionChangeCallback = null;
    bimSearchViewer.onSelectionChangeQuickStats = null;
}

/**
 * Called when the user selects categories
 * @param {Array<string>} categories - selected categories.
 * @returns {DataForMenus} DataForMenus instance that should change the sidebar menu.
 */
export async function updateCategory(categories, returnMethod) {
    var res = await bimSearch.selectCategory(categories, null);

    bimSearchViewer.isolateElements(bimSearch.categoryElements);

    logSelectCategory(bimSearch);

    return res;
}

// called after categories selection
// get property name as string
// returns PropertyInfo for this property based on categories slection
export async function propertyInfo(property) {
    return await bimSearch.getProperyInfo(property);
}

// called when the user change the parameter that the 2nd filter is done by
// receives the parameter we want to filter by
// return DataForMenus that should change the left menu
export async function updateFilterBy(filter) {
    return await bimSearch.changeFilterBy(filter);
}

// called when the user apply the second filter value selection
// receives an array of the selected values
// return DataForMenus that should change the left menu
export async function applySecondFilter(values) {
    var res = await bimSearch.selectSecondFilterValues(values);
    bimSearchViewer.isolateElements(bimSearch.filteredElements);
    return res;
}

// called when the user apply the second filter using the operator option
// operator - chosen operator operatorValues enum
// return DataForMenus that should change the left menu
export async function applySecondFilterWithOperator(operator, value) {
    var res = await bimSearch.selectSecondFilterValueWithOperator(
        operator,
        value
    );
    bimSearchViewer.isolateElements(bimSearch.filteredElements);
    return res;
}

// called when the user change the line selection in the table
// elements - an array of the coombined elements from all selected lines
// modelId - optional modelId to target specific viewer
export async function tableSelect(elements, modelId = null) {
    // CRITICAL: Get the correct viewer for this model
    let targetViewer = null;
    
    try {
        const { getCurrentNamespace } = require('../crossTabSync');
        const currentNs = getCurrentNamespace();
        
        if (modelId) {
            targetViewer = viewersByModelId[modelId];
        } else {
            // CRITICAL: If no modelId but namespace is set, use viewer from viewersByModelId
            // Don't use global viewer if namespace is set - this prevents cross-model sync
            if (currentNs) {
                targetViewer = viewersByModelId[currentNs];
                if (!targetViewer) {
                    // No viewer in map for this namespace - block to prevent cross-model sync
                    return;
                }
            } else {
                // No namespace set - use global viewer (backward compatibility)
                targetViewer = bimSearchViewer;
            }
        }
        
        // CRITICAL: If modelId provided, verify it matches current namespace
        // This prevents cross-model viewer updates
        if (modelId && targetViewer) {
            if (currentNs && currentNs !== modelId) {
                // ModelId doesn't match current namespace - don't update viewer
                return;
            }
        }
    } catch (err) {
        console.error('[tableSelect] Error getting namespace', err);
    }
    
    if (!targetViewer) {
        return;
    }

    const isEmptyArray = Array.isArray(elements) && elements.length === 0;
    if (elements == null || isEmptyArray) {
        // Clear current table selection (and restore isolation to current filtered set if available)
        try {
            if (targetViewer.bimSearch?.filteredElements) {
                targetViewer.isolateElements(targetViewer.bimSearch.filteredElements);
            } else if (targetViewer?.viewer) {
                targetViewer.viewer.clearSelection();
            }
        } catch { }
        return;
    }

    targetViewer.selectElements(elements);
    logTableLineSelected();
}

// used to isolate the table on 0 lines collection
// modelId - optional modelId to target specific viewer
export async function isolateElements(elements, modelId = null) {
    // CRITICAL: Get the correct viewer for this model
    let targetViewer = null;
    
    if (modelId) {
        targetViewer = viewersByModelId[modelId];
    } else {
        // CRITICAL: If no modelId but namespace is set, use viewer from viewersByModelId
        // Don't use global viewer if namespace is set - this prevents cross-model sync
        try {
            const { getCurrentNamespace } = require('../crossTabSync');
            const currentNs = getCurrentNamespace();
            if (currentNs) {
                targetViewer = viewersByModelId[currentNs];
                if (!targetViewer) {
                    // No viewer in map for this namespace - block to prevent cross-model sync
                    return;
                }
            } else {
                // No namespace set - use global viewer (backward compatibility)
                targetViewer = bimSearchViewer;
            }
        } catch {
            // Error getting namespace - use global viewer (backward compatibility)
            targetViewer = bimSearchViewer;
        }
    }
    
    // CRITICAL: If modelId provided, verify it matches current namespace
    if (modelId && targetViewer) {
        try {
            const { getCurrentNamespace } = require('../crossTabSync');
            const currentNs = getCurrentNamespace();
            if (currentNs && currentNs !== modelId) {
                // ModelId doesn't match current namespace - don't update viewer
                return;
            }
        } catch {}
    }
    
    if (!targetViewer) {
        return;
    }
    
    targetViewer.isolateElements(elements);
}

// called when the user selectes a template
// templateName - chosen template
// return DataForMenus that should change the left menu
// normally getSchedule should be called immidiately after to recieve the table
export async function chooseTemplate(templateName, templateFolder = "Default") {
    var res = await bimSearch.selectTemplate(templateName, templateFolder);

    bimSearchViewer.isolateElements(bimSearch.filteredElements);

    logSelectCategory(bimSearch);
    logTemplate(templateName);

    return res;
}

// Gets the data for the table
// properties - selected properties
// groupingParameters - selected grouping paramters
// return DataForTable object
export async function getSchedule(properties, groupingParameters) {

    logCreateSchedule(bimSearch, properties, groupingParameters);
    var res;

    var task = bimSearch.createSchedule(properties, groupingParameters);

    bimSearchViewer.isolateElements(bimSearch.filteredElements);

    res = await task;

    return res;
}

// Should be called whenever the viewer div element changes size
export function resizeViewer() {
    if (bimSearchViewer && bimSearchViewer.viewer) bimSearchViewer.viewer.resize();
}

// return a dictonary object
// category name --> [] of QuickDataField
export function getDetailedSelectionInfo() {
    // If TimeBar is active (tempFilter elements are stored), use those instead of viewer selection
    if (isTimelineActive && timelineFilteredElements && timelineFilteredElements.length > 0) {
        return bimSearch.getDataByCategory(timelineFilteredElements);
    }
    
    // Get viewer selection
    var selectionData = bimSearchViewer.getFullSelectionData();
    
    // If selection is empty but there are filtered elements (from regular filters, not TimeBar), use those as fallback
    if (!selectionData || Object.keys(selectionData).length === 0) {
        if (bimSearch?.filteredElements && bimSearch.filteredElements.length > 0) {
            return bimSearch.getDataByCategory(bimSearch.filteredElements);
        }
    }
    
    return selectionData;
}

// Get current view screenshot
export async function viewerGetScreenShot(width, height, callback) {
    try {
        if (bimSearchViewer && bimSearchViewer.viewer && typeof bimSearchViewer.viewer.getScreenShot === 'function') {
            bimSearchViewer.viewer.getScreenShot(width, height, callback);
        } else {
            if (callback) {
                callback(null);
            }
        }
    } catch (error) {
        logError("FrontEnd.src.utils.builder.IScheduleLogic.viewerGetScreenShot", error.toString());
        if (callback) {
            callback(null);
        }
    }
}

// return QuickInfo
export async function getTemplateQuickInfo(templateName, TemplateFolder) {
    return await bimSearch.getTemplateInfo(templateName, TemplateFolder);
}

// Craete new template folder
// returns templates folders // Dictionary<string, string> (after the change)
// throw "Folder already exist"
export function addTemplateFolder(folderName) {

    return bimSearch.addTemplateFolder(folderName);
}

// Delete a template folder
// returns templates folders // Dictionary<string, string> (after the change)
// throw "Folder doesn't exist"
export function deleteTemplateFolder(folderName) {
    return bimSearch.deleteTemplateFolder(folderName);
}

// rename a template folder
// returns templates folders // Dictionary<string, string> (after the change)
// throw "Folder already exist"
// throw "Folder doesn't exist"
export function renameTemplateFolder(originalName, newName) {
    return bimSearch.renameTemplateFolder(originalName, newName);
}

//Save a new BIMSearch template
// Parameters:
//      templateFolder - string
//      title - template namne - string
//      description - string
//      properties - selected properties [] of string
//      groupBy - selected grouping optrions - [] of string
//      searchValue - current table search value - string
// returns-
//      templates folders // Dictionary<string, string> (after the change)
// Comment-
//      If template already exist resave it - responsibility to confirm with the user outside this function
export function saveTemplate(templateFolder, title, description, properties, groupBy, searchValue, customColumns = null, colorScheme = null, settings = null) {
    logCreateTemplate(bimSearch, properties, groupBy, title, description);

    return bimSearch.saveTemplate(templateFolder, title, description, properties, groupBy, searchValue, customColumns, colorScheme, settings);
}

// deletes a new BIMSearch template
// Parameters:
//      templateFolder - string
//      title - template namne - string
// returns-
//      templates folders // Dictionary<string, string> (after the change)
// throws-
//      "can't find template"
export function deleteTemplate(templateFolder, title) {
    return bimSearch.deleteTemplate(templateFolder, title);
}

// Edit a new BIMSearch template
// throws-
//      "can't find template"
//      "new name already taken"
// returns-
//      templates folders // Dictionary<string, string> (after the change)
export function editTemplate(ogrinalTemplateFolder, originalTitle, newTemplateFolder, newTitle, newDescription) {
    return bimSearch.editTemplate(ogrinalTemplateFolder, originalTitle, newTemplateFolder, newTitle, newDescription);
}

// create a new collection, doesn't save it on DB
// return collection
// parameters:
//  elements, elements of selected lines, same a passed to tableSelect
export async function createCollection(elements, properties, groupedBy, customColumns = null) {
    var collection = new Collection(properties, groupedBy, customColumns);
    await bimSearch.addElementsToCollection(collection, elements);
    return collection;
}

// return DataForTable
export async function getCollectionTable(collection) {
    logOpenSelectionSet(bimSearch, collection);

    return await bimSearch.getCollectionTable(collection);
}

// Save a collection in the database for this model
// throw "selection set already exist";
export async function saveCollection(collection, title, folder, descreption, color) {
    logCreateSelectionSet(bimSearch, title, descreption, collection);
    
    await bimSearchViewer.saveCollection(collection, title, folder, descreption, color);
}

// returns-
//      collection after change
// throw "selection set doesn't exist";
export async function addToCollection(elements, title, folder) {
    
    return await bimSearchViewer.addToCollection(elements, title, folder);
}

export async function removeFromCollection(elements, title, folder) {
    return await bimSearchViewer.removeFromCollection(elements, title, folder);
}

export async function removeFromTempCollection(elements, collection) {
    await bimSearch.removeElementsFromCollection(collection, elements);
    return collection;
}

// returns-
//      collection after change
export async function addToTempCollection(elements, collection) {
    await bimSearch.addElementsToCollection(collection, elements);
    return collection;
}

// returns-
//      [] of strings
export async function getCollectionsFolders() {
    var collections = await bimSearchViewer.getCollections();
    var collectionFolders = Object.keys(collections.collectionsByFolder);
    collectionFolders.sort();

    return collectionFolders;
}

// returns-
//      [] of collection
export async function getCollectionsForFolder(folder) {
    var collections = await bimSearchViewer.getCollections();
    return collections.collectionsByFolder[folder];
}


// returns-
//      [] of strings
export async function getReportFolders() {
    return await bimSearch.getReportFolders()
}


// returns-
//      [] of Collection
export async function getReportCollectionsForFolder(folderName) {
    return bimSearch.getReportCollectionsForFolder(folderName)
}


// returns-
//      QuickInfo
export async function getCollectionInfo(collection) {
    return await bimSearch.getCollectionInfo(collection);
}

// throws-
//      "can't find selection set"
//      "selection set already exist" - if new name already exist
export async function editCollection(oldTitle, oldFolder, newTitle, newFolder, newDescreption, newColor) {
    await bimSearchViewer.editCollection(oldTitle, oldFolder, newTitle, newFolder, newDescreption, newColor);
}


// returns-
//      collection after change
// throw "selection set doesn't exist";
export async function addSelectionToCollection(title, folder) {
    var selection = bimSearchViewer.viewer.getSelection();
    return await addToCollection(selection, title, folder);
}

export async function removeSelectionFromCollection(title, folder) {
    var selection = bimSearchViewer.viewer.getSelection();
    return await removeFromCollection(selection, title, folder);
}

export async function removeSelectionFromTempCollection(collection) {
    var selection = bimSearchViewer.viewer.getSelection();
    return await removeFromTempCollection(selection, collection);
}

export async function createTempCollectionFromSelection() {
    var selection = bimSearchViewer.viewer.getSelection();
    var properties = ["Type Name", "Description", "Length", "Area", "Volume"];
    var groups = [];
    return await createCollection(selection, properties, groups);
}

// throws-
//      "can't find selection set"
export async function deleteCollection(title, folder) {
    await bimSearchViewer.deleteCollection(title, folder);
}

// Color an array of collections
// input: Collection[]
export function colorCollections(collections) {
    bimSearchViewer.colorCollections(collections);
}

// Craete new collection folder
// returns collections folders // [] of strings (after the change)
// throw "folder already exist"
export async function addCollectionFolder(folderName) {
    await bimSearchViewer.addCollectionFolder(folderName);
    return await getCollectionsFolders();
}

// Delete a collection folder
// returns collection folders // [] of strings (after the change)
// throw "folder doesn't exist"
export async function deleteCollectionFolder(folderName) {
    await bimSearchViewer.deleteCollectionFolder(folderName);
    return await getCollectionsFolders();
}

// Rename a collection folder
// returns collection folders // [] of strings (after the change)
// throw "Folder already exist"
// throw "Folder doesn't exist"
export async function renameCollectionFolder(originalName, newName) {
    await bimSearchViewer.renameCollectionFolder(originalName, newName);
    return await getCollectionsFolders();
}


// return a dictonary object
// category name --> [] of QuickDataField
export async function getDetailedCollectionInfo(collection) {
    return await bimSearch.getDetailedCollectionInfo(collection);
}

// register 3 call-backs for the right click menu
export function registerRightClickMethods(saveAsCollection, copyToNewCollection, unassignSelection, setCustomParameters = null) {
    bimSearchViewer.registerRightClickMethods(saveAsCollection, copyToNewCollection, unassignSelection, setCustomParameters);
}

// unregister 3 call-backs for the right click menu
export function unregisterRightClickMethods() {
    bimSearchViewer.unregisterRightClickMethods();
}

export async function getSelection() {
  var selection = bimSearchViewer.viewer.getSelection();
  return selection;
}

export async function isolateSelection() {
    bimSearchViewer.isolateSelection();
}

export async function hideSelection() {
    bimSearchViewer.hideSelection();
}

export async function showAll() {
    bimSearchViewer.showAll();
}

// change ghosting mode, receives true/false - true activate ghosting, false disables it
// when opening a model ghosting will always be on
export async function toggleGhosting(onOff) {
    bimSearchViewer.toggleGhosting(onOff);
}

// clear all coloring from model
export async function clearColors() {
    bimSearchViewer?.clearColors();
}

// get elements matching filtered items
const cutFilteredElements = (elements) =>
  bimSearch.filteredElements && bimSearch.filteredElements.length
    ? elements.filter((el) => bimSearch.filteredElements.includes(el))
    : elements;

// color an array of elements in the given color
export async function colorElements(elements, color) {
    bimSearchViewer.colorElements(cutFilteredElements(elements), color);
}

// return: result class
export async function applyFiltesrs(filtersSchema) {
    var res = await bimSearch?.applyFilters(filtersSchema);
    bimSearchViewer.isolateElements(bimSearch.filteredElements);
    return res;
}

// This function gets a filter scheme, find all elements in model that passes the filter scheme, isolate them, and return counter data
//  filtersSchema - input filters scheme
//  return - DataForMenus, containing quickCounterData
export async function tempFilter(filtersSchema) {
    var filteredElements = await bimSearch?.filterElements(bimSearch.allElements, filtersSchema);
    // Store filtered elements for use in getDetailedSelectionInfo when TimeBar is active
    timelineFilteredElements = filteredElements || [];
    isTimelineActive = true; // Mark TimeBar as active
    var res = new DataForMenus();
    res.quickCounterData = bimSearch.getCounterData(filteredElements);
    bimSearchViewer.isolateElements(filteredElements, true);
    return res;
}

// This function clear tempFilter elements
export async function clearTimelineTempFilter() {
    try {
        // Clear stored TimeBar filtered elements and mark TimeBar as inactive
        timelineFilteredElements = null;
        isTimelineActive = false;
        const elements = bimSearch?.allElements || [];
        if (bimSearchViewer?.showAll) {
            bimSearchViewer.showAll();
        } else {
            bimSearchViewer.isolateElements(elements);
        }
        const res = new DataForMenus();
        res.quickCounterData = bimSearch.getCounterData(elements);
        return res;
    } catch {
        return { quickCounterData: [] };
    }
}

// return the date range for a date parameter
//  dateParameter - date parameter name
//  returns {} with fields min and max
//  Min and max will be null if there is no value for the parameter in the model
export async function getDateRange(dateParameter) {
    var res = await bimSearch?.getDateRange(dateParameter);
    return res;
}

// function to get values of all common property of an element
//      elementId - viewer ID of the element we want go get data for
// return:
//          Dictionary (JS object) - where keys are properties names and values is the property value
export async function getCommonParametersValues(elementId) {
    return await bimSearch.getCommonParametersValues(elementId);
}


// function to get values of all common property of selected elements
//      elementIds - array of viewer ID of the elements we want go get data for
// return:
//          Dictionary (JS object) - where keys are element ids and value is Object key properties names and value is the property value
export async function getCommonParametersValuesByIds(elementIds) {
    const result = {}
    for (const elemId of elementIds) {
        const parametersValues =  await getCommonParametersValues(elemId);
        
        result[elemId] = parametersValues
    }

    return result
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


// function to create custom parameter for the currently loaded model
//      name - string
//      type - valueTypes enum
//      values - array of possible values for enum paramter, will be null for any other type
// throws exception
//      "Name not allowed"
//      "Parameter with given name already exists"
export async function createCustomParameter(name, type, values = null) {
    await bimSearch.createCustomParameter(name, type, values);
}

// function to edit custom parameter for the currently loaded model
// allowed edits
//      name - allowed for all types
//      type - only singleEnum (15) -> multiEnum (16)
//      values - for types enum parameter the user can change optional values
// params
//      oldName - string
//      newName - string
//      prevType - valueTypes enum, existing type of parameter
//      selectedType - valueTypes enum, type submitted by the user
//      values - array of possible values for enum parameter, will be null for any other type      
// throws exception
//      "Name not allowed"
//      "Parameter with given name already exists"
//      "Parameter wasn't found"
export async function editCustomParameter(oldName, newName, prevType, selectedType, values = null) {
    
    const canEditCustomParameter = (prevType === selectedType) ||
    (selectedType === valueTypes.customEnumMulty && prevType === valueTypes.customEnumSingle)

    if (canEditCustomParameter) {
        await bimSearch.editCustomParameter(oldName, newName, prevType, selectedType, values)
    } else {
        await bimSearch.deleteCustomParameter(oldName);
        await bimSearch.createCustomParameter(newName, selectedType, values);
    }
}

// return [] of objects of CustomParameter class
export async function getCustomParameters() {
    var res = await bimSearch.getCustomParameters();
    return res;
}

// return [] of objects of CustomParameter class
export async function getDateParameters() {
    var res = await bimSearch.getDateParameters();
    return res;
}

// Get the value of the named parameter for the given element
//      parameterName - name of the parameter to set
//      element - element ID
//      returns - the value of the parameter for this element
//      throws exception
//          "Parameter not found"
export async function GetCustomParameterValue(parameterName, element) {
    return await bimSearch.getCustomParameterValue(parameterName, element);
}

// Set the value of the named parameter for all elements to the given value
//      parameterName - name of the parameter to set
//      elements - [] of elements IDs
//      value - the value we want to set
//      throws exception
//          "Parameter not found"
export async function setCustomParameterValues(parameterName, elements, value) {
    await bimSearch.setCustomParameterValues(parameterName, elements, value);
}


// Set the value of the named parmaeter for all elements currently selected in viewer
//      parameterName - name of the parameter to set
//      value - the value we want to set
//      throws exception
//          "Parameter not found"
export async function setCustomParameterValuesOfSelection(parameterName, value) {
    var elements = await getSelection();
    await bimSearch.setCustomParameterValues(parameterName, elements, value);
}

// function to delete custom parameter for the currently loaded model
//      name - string
// throws exception
//      "Parameter wasn't found"
export async function deleteCustomParameter(name) {
    await bimSearch.deleteCustomParameter(name);
}

// function to create the execution report,
// called when the user presses the button
// finished when the report is generated but doesn't return anything, can get the result through other methods:
// getFullExecutionReport, getReportFolders, getReportCollectionsForFolder
export async function generateExecutionReport() {
    await bimSearch.generateExecutionReport()
}

// return all the data needed for the excels of the execution reports
// returns ExecutionReport class
export async function getFullExecutionReport() {
    return await bimSearch.getFullExecutionReport()
}

export async function closeModel() {
    await bimSearchViewer.clearMemomry();
    bimSearchViewer = null;
    bimSearch = null;
}
export function setIsTranslateModelEnabledBS(value) {
    if (bimSearch) {   
        bimSearch.isTranslateModelEnabled = value
    }
}
/**
 * Called when isTranslateModelEnabled is false and the user turns it on.
 * Meant for translating categories, properties and groups that are already in the frontend.
 * @param {Array<string>} expressions
 * @returns {Array<string>}
 */
export async function translateExpressions(expressions) {
    const result = await bimSearch.handleHebrewTranslation(expressions)
    return result
}
// function to register a handler for when element selection is changed in Autodesk viewer. used for DraggableCustomParameters.
export async function registerOnSelectionChangeCustomParameters(callback) {
    bimSearchViewer.onSelectionChangeCustomParameters = callback
}
// function to unregister a handler for when DraggableCustomParameters component unmounts.
export async function unregisterOnSelectionChangeCustomParameters() {
    bimSearchViewer.onSelectionChangeCustomParameters = null;
}
export function invokeCustomParametersButtonOnClick() {
    bimSearchViewer.customParametersButton.onClick();
}
// Register a function that will be invoked every time the selection changes in adsk viewer.
// The registered function receives 1 argument - selection of adsk viewer. 
export function registerExportSelection(callback) {
    if (callback && bimSearchViewer) {
        bimSearchViewer.trackSelection = callback
    }
}
// register a function that will erase the values of the custom properties from redux.
// essentially when only 1 element is selected in the viewer we will have the values and for any other case
// we would like to delete them.
export function registerClearCustomPropertiesValuesFromRedux(callback) {
    if (callback) {
        bimSearchViewer.clearCustomParametersValuesFromRedux = callback
    }
}
// saves last position and size of custom parameters window to user's local storage.
// size is taken from #custom-parameters-resizableBox
// position taken from its container #draggable-div
export function setCPWindowPositionToLocalStorage() {
    try {

        const cprElement = document.querySelector('#custom-parameters-resizableBox');
        const cprPosition = cprElement.getBoundingClientRect();
        
        const ddElement = document.querySelector('#draggable-div');
        const ddPosition = ddElement.getBoundingClientRect();
        
        const savedPosition = {
            top: ddPosition.top,
            left: ddPosition.left,
            right: ddPosition.right,
            bottom: ddPosition.bottom,
            width: cprPosition.width,
            height: cprPosition.height
        };
        localStorage.setItem("BIMSearchCPWindow", JSON.stringify(savedPosition))
    } catch {
        
    }
}
// gets last position and size of custom parameters window from user's local storage.
// if does not exist / if out of screen - returns default size and position.
export function getCPWindowPositionFromLocalStorage() {
    const defaultPositioning = {
        top: window.innerHeight * 0.3,
        left: window.innerWidth * 0.4,
        right: 0,
        bottom: 0,
        width: 300,
        height: 400
    }
    try {
        const savedPosition = localStorage.getItem("BIMSearchCPWindow")
        if (!savedPosition) {return defaultPositioning}

        const savedPositionJSON = JSON.parse(savedPosition)
        const { top, left, right, bottom, width, height } = savedPositionJSON;

        if (   top < 0
            || top > window.innerHeight
            || left + 0.5 * width < 0
            || right - 0.5 * width > window.innerWidth
            || width <= 0
            || height <= 0) {
            throw new Error("Position is out of bounds.");
        }
        return savedPositionJSON
    } catch (e) {
        return defaultPositioning
    }

}

/**
 * @param {Array<Array<string>>} expressions
 * @returns {Array<string>}
 */
export function returnUniqueStrings(expressions) {
    const set = new Set(expressions.filter(Boolean).flat())
    if (set.size) {
        return [...set]
    }
    return []
}

/**
 * @returns {Array<string>} Common properties in order in which they should be displayed on custom parameters menu.
 */
export function getCommonPropertiesInOrder() {
    if (bimSearch) {
        return bimSearch.getCommonPropertiesNames()
    }
    return []
}

// local instances
var bimSearchViewer; // autodesk viewer (legacy, kept for backward compatibility)
var viewersByModelId = {}; // Store viewers by modelId to support multiple models
var bimSearch; // Bim search filtered data iterator
var timelineFilteredElements = null; // Store filtered elements from TimeBar tempFilter for use in getDetailedSelectionInfo
var isTimelineActive = false; // Flag to track if TimeBar is currently active